import React, { useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { formatDateFromServer } from "~/libs/getFormatDate";
import { TrustedPerson } from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

type ActiveOption = {
  value: string;
  label: string;
};

type TrustedPersonsFormData = {
  powerOfAttorney: string;
  fio: string;
  shortFio: string;
  descriptionContract: string;
  active: ActiveOption;
  date: Date | null;
  endDate: Date | null;
};

interface TrustedPersonsFormProps {
  currentItem?: TrustedPerson;
  onSubmit: (data: TrustedPersonsFormData) => void;
  formId: string;
}

const TrustedPersonsForm = ({
  currentItem,
  onSubmit,
  formId,
}: TrustedPersonsFormProps) => {
  const { t } = useTranslation();

  const schema = yup.object().shape({
    powerOfAttorney: yup.string().required(t("validation.required")),
    fio: yup.string().required(t("validation.required")),
    shortFio: yup.string().required(t("validation.required")),
    descriptionContract: yup.string().required(t("validation.required")),
  });

  const activeOptions: ActiveOption[] = [
    { value: "active", label: "Да" },
    { value: "inactive", label: "Нет" },
  ];

  const transformStatus = (status?: boolean) =>
    status === false ? activeOptions[1] : activeOptions[0];

  const { handleSubmit, control, errors, setValue } =
    useForm<TrustedPersonsFormData>({
      resolver: yupResolver(schema),
      defaultValues: {
        powerOfAttorney: currentItem?.powerOfAttorney,
        fio: currentItem?.fio,
        active: transformStatus(currentItem?.active),
        shortFio: currentItem?.shortFio,
        descriptionContract: currentItem?.descriptionContract,
        date: formatDateFromServer(currentItem?.date),
        endDate: formatDateFromServer(currentItem?.endDate),
      },
    });

  useEffect(() => {
    if (currentItem) {
      setValue("powerOfAttorney", currentItem.powerOfAttorney);
      setValue("fio", currentItem.fio);
      setValue("shortFio", currentItem.shortFio);
      setValue("descriptionContract", currentItem.descriptionContract);
      setValue("date", formatDateFromServer(currentItem?.date));
      setValue("endDate", formatDateFromServer(currentItem?.endDate));
      setValue("active", transformStatus(currentItem.active));
    }
  }, [currentItem, setValue]);

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="powerOfAttorney"
          control={control}
          label={t("trustedPersons.table.powerOfAttorney")}
          error={errors["powerOfAttorney"]?.message}
        />
        <Field
          type="datePicker"
          name="date"
          control={control}
          label={t("trustedPersons.table.date")}
          format="DD.MM.YYYY"
        />
        <Field
          type="input"
          name="fio"
          control={control}
          label={t("trustedPersons.table.fio")}
          error={errors["fio"]?.message}
        />
        <Field
          type="datePicker"
          name="endDate"
          control={control}
          label={t("trustedPersons.table.endDate")}
          format="DD.MM.YYYY"
        />
        <Field
          type="select"
          name="active"
          control={control}
          label={t("trustedPersons.table.active")}
          options={activeOptions}
          labelInValue
        />
        <Field
          type="input"
          name="shortFio"
          control={control}
          label={t("trustedPersons.table.shortFio")}
          error={errors["shortFio"]?.message}
        />
        <Field
          type="input"
          name="descriptionContract"
          control={control}
          label={t("trustedPersons.table.descriptionContract")}
          error={errors["descriptionContract"]?.message}
        />
      </Flex>
    </form>
  );
};

export default TrustedPersonsForm;
